/* eslint-disable react/prop-types */
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { LineItemTypes, PartTypes } from '@parallel-fluidics/shared/constants';
import { getPPartId, pPartFromFirestore } from '@parallel-fluidics/shared/models/projects/parts';
import useProductVersions from 'features/products/hooks/useProductVersions';
import useSearchProducts from 'features/products/hooks/useSearchProducts';
import {
  collection,
  getFirestore,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useFormikContext } from 'formik';
import useProjects from 'hooks/use-projects';
import useRealtimeDocs from 'hooks/use-realtimeDocs';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

const lineItemTypeOptions = [
  { label: 'Product', value: LineItemTypes.PRODUCT },
  { label: 'On Demand', value: LineItemTypes.ON_DEMAND },
];

export const defaultProductLineItem = Object.freeze({
  type: LineItemTypes.PRODUCT,
  productDocId: '',
  productVersionId: '',
  sku: '',
  name: '',
  projectId: '',
  projectDocId: '',
  pPartId: '',
  pPartDocId: '',
  description: '',
  quantity: null,
  unitPrice: null,
  leadtimeDays: null,
});

const defaultOnDemandLineItem = Object.freeze({
  type: LineItemTypes.ON_DEMAND,
  projectId: '',
  projectDocId: '',
  pPartId: '',
  pPartDocId: '',
  bonding: '',
  name: '',
  description: '',
  quantity: null,
  unitPrice: null,
  leadtimeDays: null,
});

const toNumberOrNull = (value) => {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

function ProductFields({ item, index }) {
  const { errors, setFieldValue } = useFormikContext();
  const [skuFilter, setSkuFilter] = useState('');
  const { products, isLoading: productsLoading } = useSearchProducts(skuFilter);
  const {
    data: versions = [],
    isLoading: versionsLoading,
  } = useProductVersions(item.productDocId || null);

  const productOptions = useMemo(() => products.map((product) => ({
    label: product.sku,
    value: product.id,
    description: product.name,
    tags: [product.activeVersionId ? `active: ${product.activeVersionId}` : 'no active version'],
    product,
  })), [products]);

  const selectedProductOption = useMemo(() => (
    productOptions.find((option) => option.value === item.productDocId) || null
  ), [item.productDocId, productOptions]);

  const versionOptions = useMemo(() => versions.map((version) => ({
    label: version.id,
    value: version.id,
    description: `$${version.unitPrice} - ${version.leadtimeDays ?? '?'} days - ${version.status}`,
    version,
  })), [versions]);

  const selectedVersionOption = useMemo(() => (
    versionOptions.find((option) => option.value === item.productVersionId) || null
  ), [item.productVersionId, versionOptions]);

  const handleProductChange = useCallback(({ detail }) => {
    const { product } = detail.selectedOption;
    setFieldValue(`lineItems[${index}].productDocId`, product.id);
    setFieldValue(`lineItems[${index}].sku`, product.sku);
    setFieldValue(`lineItems[${index}].name`, product.name);
    setFieldValue(`lineItems[${index}].description`, product.description || '');
    setFieldValue(`lineItems[${index}].productVersionId`, product.activeVersionId || '');
    setFieldValue(`lineItems[${index}].unitPrice`, null);
    setFieldValue(`lineItems[${index}].leadtimeDays`, null);
    setFieldValue(`lineItems[${index}].projectId`, '');
    setFieldValue(`lineItems[${index}].projectDocId`, '');
    setFieldValue(`lineItems[${index}].pPartId`, '');
    setFieldValue(`lineItems[${index}].pPartDocId`, '');
  }, [index, setFieldValue]);

  const handleVersionChange = useCallback(({ detail }) => {
    const { version } = detail.selectedOption;
    setFieldValue(`lineItems[${index}].productVersionId`, version.id);
  }, [index, setFieldValue]);

  useEffect(() => {
    if (!item.productVersionId || !versions.length) return;
    const version = versions.find(({ id }) => id === item.productVersionId);
    if (!version) return;

    setFieldValue(`lineItems[${index}].unitPrice`, version.unitPrice);
    setFieldValue(`lineItems[${index}].leadtimeDays`, version.leadtimeDays);
    setFieldValue(`lineItems[${index}].projectId`, version.projectId || '');
    setFieldValue(`lineItems[${index}].projectDocId`, version.projectDocId || '');
    setFieldValue(`lineItems[${index}].pPartId`, version.pPartId || '');
    setFieldValue(`lineItems[${index}].pPartDocId`, version.pPartDocId || '');
  }, [index, item.productVersionId, setFieldValue, versions]);

  return (
    <ColumnLayout columns={3}>
      <FormField label="SKU" errorText={errors?.lineItems?.[index]?.productDocId}>
        <Select
          filteringType="manual"
          statusType={productsLoading ? 'loading' : 'finished'}
          loadingText="Loading products"
          onLoadItems={({ detail }) => setSkuFilter(detail.filteringText)}
          selectedOption={selectedProductOption}
          onChange={handleProductChange}
          options={productOptions}
          placeholder="Search by SKU"
          empty="No active products found"
        />
      </FormField>

      <FormField label="Version" errorText={errors?.lineItems?.[index]?.productVersionId}>
        <Select
          disabled={!item.productDocId}
          statusType={versionsLoading ? 'loading' : 'finished'}
          loadingText="Loading versions"
          selectedOption={selectedVersionOption}
          onChange={handleVersionChange}
          options={versionOptions}
          empty="Select a SKU first"
        />
      </FormField>

      <FormField label="Name" errorText={errors?.lineItems?.[index]?.name}>
        <Input
          value={item.name || ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].name`, detail.value)}
        />
      </FormField>

      <FormField label="Quantity" errorText={errors?.lineItems?.[index]?.quantity}>
        <Input
          type="number"
          value={item.quantity ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].quantity`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Unit Price" errorText={errors?.lineItems?.[index]?.unitPrice}>
        <Input
          type="number"
          value={item.unitPrice ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].unitPrice`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Lead Time Days" errorText={errors?.lineItems?.[index]?.leadtimeDays}>
        <Input
          type="number"
          value={item.leadtimeDays ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].leadtimeDays`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Description" errorText={errors?.lineItems?.[index]?.description}>
        <Textarea
          value={item.description || ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].description`, detail.value)}
        />
      </FormField>

    </ColumnLayout>
  );
}

function OnDemandFields({ item, index }) {
  const { errors, setFieldValue } = useFormikContext();
  const { projectOptions } = useProjects({ isInternal: false });

  const selectedProject = useMemo(() => (
    projectOptions.find((option) => option.value === item.projectId) || null
  ), [item.projectId, projectOptions]);

  const partsQuery = useMemo(() => {
    if (!item.projectDocId) return null;
    return query(
      collection(getFirestore(), 'projects', item.projectDocId, 'parts'),
      where('type', '==', PartTypes.PRODUCT),
      orderBy('partId', 'desc'),
    );
  }, [item.projectDocId]);
  const { data: parts } = useRealtimeDocs({ query: partsQuery, transformData: pPartFromFirestore });

  const partOptions = useMemo(() => parts.map((part) => {
    const pPartId = getPPartId(part);
    return {
      label: `${pPartId} (${part.name})`,
      value: pPartId,
      pPartId,
      pPartDocId: part.id,
      part,
    };
  }), [parts]);

  const selectedPart = useMemo(() => (
    partOptions.find((option) => option.value === item.pPartId) || null
  ), [item.pPartId, partOptions]);

  return (
    <ColumnLayout columns={3}>
      <FormField label="Project" errorText={errors?.lineItems?.[index]?.projectId}>
        <Select
          filteringType="auto"
          selectedOption={selectedProject}
          onChange={({ detail }) => {
            const option = detail.selectedOption;
            setFieldValue(`lineItems[${index}].projectId`, option.value);
            setFieldValue(`lineItems[${index}].projectDocId`, option.id);
            setFieldValue(`lineItems[${index}].name`, option.display || option.value);
            if (option.partId) setFieldValue(`lineItems[${index}].partId`, option.partId);
            setFieldValue(`lineItems[${index}].pPartId`, '');
            setFieldValue(`lineItems[${index}].pPartDocId`, '');
          }}
          options={projectOptions}
        />
      </FormField>

      <FormField label="Part" errorText={errors?.lineItems?.[index]?.pPartId}>
        <Select
          filteringType="auto"
          disabled={!item.projectDocId}
          selectedOption={selectedPart}
          onChange={({ detail }) => {
            const option = detail.selectedOption;
            setFieldValue(`lineItems[${index}].pPartId`, option.pPartId);
            setFieldValue(`lineItems[${index}].pPartDocId`, option.pPartDocId);
          }}
          options={partOptions}
          empty="Select a project first"
        />
      </FormField>

      <FormField label="Name" errorText={errors?.lineItems?.[index]?.name}>
        <Input
          value={item.name || ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].name`, detail.value)}
        />
      </FormField>

      <FormField label="Quantity" errorText={errors?.lineItems?.[index]?.quantity}>
        <Input
          type="number"
          value={item.quantity ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].quantity`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Unit Price" errorText={errors?.lineItems?.[index]?.unitPrice}>
        <Input
          type="number"
          value={item.unitPrice ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].unitPrice`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Lead Time Days" errorText={errors?.lineItems?.[index]?.leadtimeDays}>
        <Input
          type="number"
          value={item.leadtimeDays ?? ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].leadtimeDays`, toNumberOrNull(detail.value))}
        />
      </FormField>

      <FormField label="Bonding" errorText={errors?.lineItems?.[index]?.bonding}>
        <Input
          value={item.bonding || ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].bonding`, detail.value)}
        />
      </FormField>

      <FormField label="Description" errorText={errors?.lineItems?.[index]?.description}>
        <Textarea
          value={item.description || ''}
          onChange={({ detail }) => setFieldValue(`lineItems[${index}].description`, detail.value)}
        />
      </FormField>

    </ColumnLayout>
  );
}

export default function LineItemInfo() {
  const { values, errors, setFieldValue } = useFormikContext();

  return (
    <Container header={<Header variant="h2">Line Items</Header>}>
      <SpaceBetween size="m">
        {values.lineItems.map((item, index) => {
          const itemType = item.type || LineItemTypes.PRODUCT;
          return (
            <Container
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              header={(
                <Header
                  variant="h3"
                  actions={values.lineItems.length > 1 && (
                    <Button
                      variant="icon"
                      iconName="close"
                      onClick={() => {
                        setFieldValue('lineItems', values.lineItems.filter((_, i) => i !== index));
                      }}
                    />
                  )}
                >
                  {`Line Item ${index + 1}`}
                </Header>
              )}
            >
              <SpaceBetween size="s">
                <FormField label="Line Item Type" errorText={errors?.lineItems?.[index]?.type}>
                  <Select
                    selectedOption={lineItemTypeOptions.find((option) => option.value === itemType)}
                    onChange={({ detail }) => {
                      setFieldValue(
                        `lineItems[${index}]`,
                        detail.selectedOption.value === LineItemTypes.PRODUCT
                          ? { ...defaultProductLineItem }
                          : { ...defaultOnDemandLineItem },
                      );
                    }}
                    options={lineItemTypeOptions}
                  />
                </FormField>

                {itemType === LineItemTypes.PRODUCT ? (
                  <ProductFields item={item} index={index} />
                ) : (
                  <OnDemandFields item={item} index={index} />
                )}
              </SpaceBetween>
            </Container>
          );
        })}

        <Box>
          <Button
            variant="normal"
            onClick={() => setFieldValue('lineItems', [
              ...values.lineItems,
              { ...defaultProductLineItem },
            ])}
          >
            Add Line Item
          </Button>
        </Box>
      </SpaceBetween>
    </Container>
  );
}
